import { z } from 'zod';
import { boardVisibilitySchema } from './board';
import { ulidSchema } from './id';
import { EMAIL_MAX_LENGTH, publicProfileSchema } from './user';

export const ENTERPRISE_ROLES = ['admin', 'member'] as const;
export const enterpriseRoleSchema = z.enum(ENTERPRISE_ROLES);
export type EnterpriseRole = z.infer<typeof enterpriseRoleSchema>;

export const ENTERPRISE_BOARD_POLICY_RULES = [
  'any_member',
  'admins_only',
  'nobody',
] as const;
export const enterpriseBoardPolicyRuleSchema = z.enum(ENTERPRISE_BOARD_POLICY_RULES);
export type EnterpriseBoardPolicyRule = z.infer<typeof enterpriseBoardPolicyRuleSchema>;

export const ENTERPRISE_GUEST_INVITE_RULES = [
  'anyone',
  'workspace_admins_only',
  'same_workspace_members',
  'nobody',
] as const;
export const enterpriseGuestInviteRuleSchema = z.enum(ENTERPRISE_GUEST_INVITE_RULES);
export type EnterpriseGuestInviteRule = z.infer<typeof enterpriseGuestInviteRuleSchema>;

export const enterpriseBoardVisibilityPolicySchema = z.object({
  visibility: boardVisibilitySchema,
  createRule: enterpriseBoardPolicyRuleSchema,
  deleteRule: enterpriseBoardPolicyRuleSchema,
  workspaceAdminCanControl: z.boolean(),
});
export type EnterpriseBoardVisibilityPolicy = z.infer<
  typeof enterpriseBoardVisibilityPolicySchema
>;

export const enterprisePolicySchema = z.object({
  enterpriseId: ulidSchema,
  defaultBoardVisibility: boardVisibilitySchema,
  guestInviteRule: enterpriseGuestInviteRuleSchema,
  visibilityPolicies: z.array(enterpriseBoardVisibilityPolicySchema),
});
export type EnterprisePolicy = z.infer<typeof enterprisePolicySchema>;

export const enterpriseDefaultBoardVisibilityPatchSchema = z.strictObject({
  visibility: boardVisibilitySchema,
});
export type EnterpriseDefaultBoardVisibilityPatch = z.infer<
  typeof enterpriseDefaultBoardVisibilityPatchSchema
>;

export const enterpriseGuestInviteRulePatchSchema = z.strictObject({
  guestInviteRule: enterpriseGuestInviteRuleSchema,
});
export type EnterpriseGuestInviteRulePatch = z.infer<
  typeof enterpriseGuestInviteRulePatchSchema
>;

export const enterpriseBoardVisibilityPolicyPatchSchema = z
  .strictObject({
    createRule: enterpriseBoardPolicyRuleSchema.optional(),
    deleteRule: enterpriseBoardPolicyRuleSchema.optional(),
    workspaceAdminCanControl: z.boolean().optional(),
  })
  .refine(
    (body) =>
      body.createRule !== undefined ||
      body.deleteRule !== undefined ||
      body.workspaceAdminCanControl !== undefined,
    { message: 'At least one policy field must be supplied' },
  );
export type EnterpriseBoardVisibilityPolicyPatch = z.infer<
  typeof enterpriseBoardVisibilityPolicyPatchSchema
>;

export const managedMemberLifecycleBodySchema = z.strictObject({
  confirm: z.literal(true),
});
export type ManagedMemberLifecycleBody = z.infer<typeof managedMemberLifecycleBodySchema>;

// ---------------------------------------------------------------------------
// Enterprise linkage discovery — `FR-PERM-011`, `FR-PERM-033`
// ---------------------------------------------------------------------------

/**
 * `GET /workspaces/:id/enterprise` — the one fact a workspace-scoped surface
 * needs before it can decide whether an Enterprise rule applies to it.
 *
 * Until this shape existed the web client had **no way to learn that a
 * workspace belongs to an Enterprise at all**: no route returned
 * `workspaces.enterprise_id` and none returned the caller's
 * `enterprise_members.role`, so `BoardVisibilityControl` offered
 * `enterprise_visible` unconditionally and no Enterprise policy surface could
 * be addressed with an id. That is the whole of `FE-67.1`, and the reason this
 * is a **new schema behind a new route** rather than a field bolted onto
 * `workspaceSchema`: that schema is embedded by `./boards-page`, which no
 * chunk owns alongside this one, and an Enterprise id reaching the boards page
 * is a disclosure decision nothing in the corpus asks for.
 *
 * **Every field is required, deliberately.** The optionality that governs the
 * three shipped schemas widened beside this one is a compatibility device for
 * typed literals and JSON mocks that already exist; there are none here, and
 * an optional field would hand the eventual surface a `| undefined` it can
 * never actually receive.
 *
 * STATUS CODES ARE THE WHOLE OF THIS SHAPE'S DESIGN, and they are a route
 * obligation this schema cannot express:
 *
 *   - The guard is workspace **membership**. A non-member, a soft-deleted
 *     workspace and an id that never existed all fold into **one 404** — never
 *     401, never 403. Answering either would make the route an existence
 *     oracle for the workspace *and* for the Enterprise behind it.
 *   - A member of a workspace with **no** Enterprise gets **200 with
 *     `enterpriseId: null`**, never a 404. Collapsing "not linked" into "you
 *     may not see this" makes the answer useless: `FR-PERM-011` AC1 turns on
 *     exactly the distinction between the two.
 *   - A caller who reaches a 200 is already inside a workspace of that
 *     Enterprise, so the id and **their own** role are facts they are within
 *     the scope of.
 *
 * `viewerEnterpriseRole` is the **caller's** role and never anyone else's.
 * Reading who else administers the Enterprise is `enterpriseMemberSchema`,
 * behind an Enterprise-admin guard.
 *
 * **`enterpriseId: null` implies `viewerEnterpriseRole: null`**, and Zod is
 * not asked to enforce the pairing. A `.refine()` here would turn a handler
 * bug into a 500 at serialisation time — the response schema would be
 * rejecting a row the server itself produced — which is strictly worse than
 * the integration assertion that catches it at the source. The pairing is a
 * route obligation, asserted in `enterprise.integration.test.ts`.
 *
 * No `enterpriseName`. Nothing in `FR-PERM-011`, `-033`, `-034` or `-035` asks
 * for one — `FR-PERM-033` AC1 wants the reason to name *Enterprise policy*,
 * not the Enterprise — and a field on the wire is a field the web client binds
 * to and a later block cannot remove.
 */
export const workspaceEnterpriseLinkageSchema = z.object({
  /** Echoes the subject, so a cached response cannot be misattributed. */
  workspaceId: ulidSchema,
  /** `workspaces.enterprise_id`. **`null` = this workspace has no Enterprise.** */
  enterpriseId: ulidSchema.nullable(),
  /**
   * The caller's own `enterprise_members.role` for that Enterprise, or `null`
   * when they hold no membership row in it — which a workspace member of an
   * Enterprise-linked workspace legitimately may not.
   */
  viewerEnterpriseRole: enterpriseRoleSchema.nullable(),
});

export type WorkspaceEnterpriseLinkage = z.infer<typeof workspaceEnterpriseLinkageSchema>;

/**
 * The result of `DELETE /workspaces/:id/enterprise` — `FR-PERM-036`, and the
 * caller `FR-PERM-011`'s edge case had been waiting for.
 *
 * ⚠ **A SEPARATE SCHEMA, NOT A WIDENING OF `workspaceEnterpriseLinkageSchema`.**
 * That schema serialises `GET /workspaces/:id/enterprise`, which any workspace
 * member may read; adding `narrowedBoardIds` to it would hand every member of
 * every linked workspace a list of board ids on a route that answers a
 * different question. The two shapes overlap on `workspaceId` and diverge
 * everywhere else — `viewerEnterpriseRole` is meaningless once the linkage is
 * gone, and `enterpriseId` is `null` by construction — so this is a copy, per
 * the repository rule that a shipped schema is extended on a copy and never
 * widened in place.
 *
 * **`narrowedBoardIds` is the proof the fallback ran, and that is why it is on
 * the wire at all.** `narrowEnterpriseVisibleBoardsAfterUnlink` self-guards on
 * `workspaces.enterprise_id IS NULL`, so a caller that runs it in the wrong
 * order narrows nothing and throws nothing — the returned list is the only
 * observable difference between "there was nothing at `enterprise_visible`"
 * and "the fallback never executed". Empty is therefore a legitimate answer
 * (an unlinked workspace whose boards were all private, workspace-visible or
 * public) and never an error.
 *
 * Ascending by id, so a client may compare two responses without sorting and
 * a test may assert an exact array.
 */
export const workspaceEnterpriseUnlinkResultSchema = z.object({
  /** Echoes the subject, so a cached response cannot be misattributed. */
  workspaceId: ulidSchema,
  /**
   * The boards moved from `enterprise_visible` down to `workspace_visible` by
   * this unlink, ascending. **Only that transition appears here.** `public`
   * boards are deliberately untouched — `FR-PERM-011`'s fear is a board
   * *becoming* public, and demoting one that already is would be the opposite
   * bug in the same sentence.
   */
  narrowedBoardIds: z.array(ulidSchema),
});

export type WorkspaceEnterpriseUnlinkResult = z.infer<
  typeof workspaceEnterpriseUnlinkResultSchema
>;

// ---------------------------------------------------------------------------
// Managed members — `FR-PERM-034`
// ---------------------------------------------------------------------------

/**
 * One row of `GET /enterprises/:id/members` — `FR-PERM-034`, "accounts on
 * those domains become **managed members** subject to Enterprise policy".
 *
 * **Built by extending `publicProfileSchema`**, for the reason
 * `workspaceMemberSchema` states at length: that schema is this repository's
 * one definition of what another member may read, so a member list either
 * serves exactly those fields or it has invented a second, divergent public
 * profile. Extending also makes a leak impossible to introduce quietly —
 * except for the one field below that escapes it on purpose, in writing.
 *
 * **`managed` is not the same population as membership, and that is why it is
 * a field rather than a filter.** The server's `assertManagedMember` requires
 * **both** `users.enterprise_id` matching **and** the account's email domain
 * present in `enterprise_domains`. A person can therefore hold an
 * `enterprise_members` row and not be managed, and `FR-PERM-034` grants the
 * Enterprise power over *managed* members specifically. A list that silently
 * dropped the unmanaged rows would hide members the admin can see elsewhere;
 * one that marked every row managed would claim power the requirement does
 * not grant.
 *
 * THE PRIVACY RULING, field by field rather than as a reassurance:
 *
 *   - **`email` serialises only for a managed member; it is `null`
 *     otherwise.** `FR-PERM-034`'s subject is accounts on domains *the
 *     Enterprise has itself verified*, and an admin who cannot see the address
 *     cannot verify why a row is managed. An **unmanaged** member is by
 *     definition not under Enterprise control and the requirement grants
 *     nothing over them, so their address stays `null`. This is the one field
 *     that escapes `publicProfileSchema`, and it escapes it conditionally.
 *     **The condition is a route obligation, not a `.refine()`** — see
 *     `workspaceEnterpriseLinkageSchema` for why a response schema does not
 *     police its own producer — and it is asserted in
 *     `enterprise.integration.test.ts`.
 *   - **A deactivated row is not redacted.** Name, username and avatar
 *     serialise exactly as stored. `FR-PERM-034` AC3 requires a deactivated
 *     member's comments to "remain visible and attributed", so blanking the
 *     name here would contradict the card back that still shows it.
 *     **Deactivation is not deletion** and the requirement's Behaviour says so
 *     outright ("a distinct action from removal").
 *   - **Soft-deleted accounts are excluded from the list entirely.**
 *     `assertManagedMember` already treats them as absent, and the two must
 *     agree or the list would offer a deactivate control that answers 404.
 *   - **No `deactivatedAt`.** Nothing in `FR-PERM-034` asks *when*.
 *
 * Every field is required, for `workspaceEnterpriseLinkageSchema`'s reason: a
 * new schema behind a new route has no existing literal or mock to stay
 * compatible with.
 */
export const enterpriseMemberSchema = publicProfileSchema.extend({
  /** `enterprise_members.role`, `CHECK role IN ('admin','member')`. */
  role: enterpriseRoleSchema,
  /**
   * `users.enterprise_id` matches **and** the account's email domain is in
   * `enterprise_domains` — the two-part test `assertManagedMember` applies.
   */
  managed: z.boolean(),
  /** `users.deactivated_at IS NOT NULL`. A state, never a redaction. */
  deactivated: z.boolean(),
  /**
   * `users.email`, **`null` unless `managed` is true**.
   *
   * A width-bounded string rather than `emailSchema`: the format check is a
   * *write* boundary, and a read schema that enforced it would refuse to serve
   * a row that legitimately exists. Same division `publicProfileSchema.avatarUrl`
   * documents.
   */
  email: z.string().max(EMAIL_MAX_LENGTH).nullable(),
  /**
   * `enterprise_members.created_at`, serialised as the driver returns it.
   *
   * A bounded string rather than `z.iso.datetime()`, for the reason
   * `workspaceMemberSchema.joinedAt` states in full: the column is
   * `timestamptz(3)` and `pg` hands it back in Postgres' own spelling
   * (`2026-08-10 12:00:00.123+00`), which is a real instant and not an ISO
   * 8601 `T`/`Z` form. Pinning it here would make this schema reject the value
   * the database actually produced.
   */
  joinedAt: z.string().min(1),
});

export type EnterpriseMember = z.infer<typeof enterpriseMemberSchema>;

export const enterpriseMemberListSchema = z.array(enterpriseMemberSchema);
